import React, { useState, useEffect } from "react";
import {
  Form,
  DropdownButton,
  Dropdown,
  Collapse,
  Button,
} from "react-bootstrap";
import ReactDatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { useFormikContext } from "formik";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import useWindowSize from "../../../hooks/useWindowSize";
import {
  DateRangePicker,
  defaultStaticRanges,
  createStaticRanges,
  defaultInputRanges,
  DateRange,
} from "react-date-range";
import "react-date-range/dist/styles.css"; // main css file
import "react-date-range/dist/theme/default.css";
import { TextField, IconButton } from "@mui/material";
import { FaCalendarAlt } from "react-icons/fa";
import { enGB } from "date-fns/locale";
import DateRangePickerModal from "./DateRangePickerModal";
import { format } from "date-fns";
import DatePickerModal from "./DatePickerModal";
import useDeviceType from "../../../hooks/useDeviceType";

const CustomInput = React.forwardRef(({ value, onClick }, ref) => (
  <input
    className="custom-date-input"
    onClick={onClick}
    ref={ref}
    value={value}
    readOnly
  />
));

const ReportsFilterForm = ({
  user,
  tenantsList,
  managersList,
  customersList,
  setGroupByPeriod,
  setUsageType,
  demoMode,
}) => {
  const { t } = useTranslation();
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const [usageOpen, setUsageOpen] = useState(false);
  const [orderOpen, setOrderOpen] = useState(false);
  const [invoiceListOpen, setInvoiceListOpen] = useState(false);

  const dummyCustomers = [
    { id: "cust-001", fullName: "Alpha Tech Solutions" },
    { id: "cust-002", fullName: "Beta Mobile Ltd." },
    { id: "cust-003", fullName: "Gamma Connectivity" },
    { id: "cust-004", fullName: "Delta IoT Services" },
    { id: "cust-005", fullName: "Epsilon Networks" },
    { id: "cust-006", fullName: "Zeta Devices" },
    { id: "cust-007", fullName: "Eta Telecom" },
    { id: "cust-008", fullName: "Theta Enterprises" },
    { id: "cust-009", fullName: "Iota Wireless" },
    { id: "cust-010", fullName: "Kappa M2M" },
  ];

  const handleToggleUsageOpen = () => {
    // Toggle the clicked date picker, closing any other that might be open
    setUsageOpen(!usageOpen);
  };

  const handleToggleOrderOpen = () => {
    setOrderOpen(!orderOpen);
  };

  const handleToggleInvoiceListOpen = () => {
    setInvoiceListOpen(!invoiceListOpen);
  };

  const filteredDefaultStaticRanges = defaultStaticRanges.filter(
    (range) =>
      range.label !== "Last Month" &&
      range.label !== "Last Week" &&
      range.label !== "Yesterday" &&
      range.label !== "This Week"
  );

  const translatedDefaultStaticRanges = filteredDefaultStaticRanges.map(
    (range) => ({
      ...range,
      label: t(`reports.${range.label}`), // Translate each label key
    })
  );

  const filteredDefaultInputRanges = defaultInputRanges.filter(
    (range) => range.label !== "days up to today"
  );

  const translatedDefaultInputRanges = filteredDefaultInputRanges.map(
    (range) => ({
      ...range,
      label: t(`reports.${range.label}`), // Translate each label key
    })
  );

  const {
    values,
    handleChange,
    handleBlur,
    touched,
    errors,
    initialValues,
    setValues,
    setFieldValue,
    handleSubmit,
    isSubmitting,
  } = useFormikContext();
  const [nextCollapse, setNextCollapse] = useState(null);
  const [reportTypeCollapse, setReportTypeCollapse] = useState(null);
  const [prevReportType, setPrevReportType] = useState(null);

  const handleSelect = (item) => {
    const { startDate, endDate } = item.selection;

    setFieldValue("usageStartDate", startDate);
    setFieldValue("usageEndDate", endDate);
  };

  useEffect(() => {
    // Close all collapses
    setNextCollapse(null);

    // Open the relevant collapse after a delay
    setTimeout(() => {
      setNextCollapse(values.simSearchCriteriaType);
    }, 300);
  }, [values.simSearchCriteriaType]);

  useEffect(() => {
    // Check if transitioning between "invoiceDetail" and "invoiceOverview"
    const isInvoiceTransition =
      (values.reportType === "invoiceDetail" &&
        prevReportType === "invoice-list") ||
      (values.reportType === "invoiceOverview" &&
        prevReportType === "invoice-list");

    // If transitioning between "invoiceDetail" and "invoiceOverview", do not reset collapse
    if (!isInvoiceTransition) {
      setReportTypeCollapse(null);
      setTimeout(() => {
        setReportTypeCollapse(values.reportType);
      }, 400);
    }

    // Update the previous reportType value
    setPrevReportType(values.reportType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.reportType]);

  return (
    <Form
      onSubmit={(e) => {
        if (isSubmitting) {
          e.preventDefault();
          return;
        }
        handleSubmit(e);
      }}
    >
      <Form.Group className="mb-3">
        <Form.Label>{t("reports.reportType")}</Form.Label>
        <Form.Select
          name="reportType"
          value={values.reportType}
          onChange={(e) => {
            setValues(initialValues);
            handleChange(e);
          }}
          onBlur={handleBlur}
          isInvalid={Boolean(touched.reportType && errors.reportType)}
        >
          <option key="usage" value="usage">
            {t("reports.usage")}
          </option>
          {/* <option key="ipAllocation" value="ipAllocation">
            IP Allocation
          </option> */}
          <option key="invoice-list" value="invoice-list">
            Invoice List
          </option>
          <option key="invoice" value="invoice">
            Invoice Detail
          </option>
          <option key="orders" value="orders">
            Orders
          </option>
          <option key="portfolio-activity" value="portfolio-activity">
            Portfolio Activity
          </option>
          <option key="customer-sessions" value="customer-sessions">
            Customer SIM Sessions
          </option>
          <option key="sim-overview" value="sim-overview">
            SIM Overview
          </option>
          <option key="sim-list" value="sim-list">
            SIM List
          </option>
          {!demoMode &&
            (user.role === "admin" ||
              user.role === "tenant" ||
              user.role === "manager" ||
              (user.role === "subaccount" &&
                (user.admin != null ||
                  user.tenant != null ||
                  user.manager != null))) && (
              <option key="customer-sim-overview" value="customer-sim-overview">
                Customer SIM Overview
              </option>
            )}
        </Form.Select>
      </Form.Group>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.usageType")}</Form.Label>
          <Form.Select
            name="usageType"
            value={values.usageType}
            onChange={(e) => {
              setUsageType(e.target.value);
              handleChange(e);
            }}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.usageType && errors.usageType)}
          >
            <option key="" value="">
              {t("reports.selectUsageType")}
            </option>
            <option key="data" value="data">
              {t("reports.data")}
            </option>
            <option key="sms" value="sms">
              {t("reports.sms")}
            </option>
            {/* <option key="sms" value="sms">
              SMS
            </option> */}
          </Form.Select>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-3">
          <Form.Label>
            {t("reports.groupBy")} <small>{t("reports.field")}</small>
          </Form.Label>
          <Form.Select
            name="groupByField"
            value={values.groupByField}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.groupByField && errors.groupByField)}
          >
            <option key="all" value="all">
              {t("reports.all")}
            </option>
            <option key="imsi" value="imsi">
              {t("reports.imsi")}
            </option>
            {values.usageType === "data" && (
              <option key="network" value="network">
                {t("reports.network")}
              </option>
            )}
            <option key="customer" value="customer">
              {t("reports.customer")}
            </option>
            {/* <option key="service" value="service">
              Service
            </option> */}
          </Form.Select>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-4">
          <Form.Label>
            {t("reports.groupBy")} <small>{t("reports.period")}</small>
          </Form.Label>
          <Form.Select
            name="groupByPeriod"
            value={values.groupByPeriod}
            onChange={(e) => {
              setGroupByPeriod(e.target.value);
              handleChange(e);
            }}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.groupByPeriod && errors.groupByPeriod)}
          >
            <option key="day" value="day">
              {t("reports.day")}
            </option>
            <option key="month" value="month">
              {t("reports.month")}
            </option>
            <option key="year" value="year">
              {t("reports.year")}
            </option>
          </Form.Select>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-3">
          {/* <Form.Label>{t("reports.period")}</Form.Label> */}
          <div className="d-flex mt-4">
            <TextField
              fullWidth
              variant="outlined"
              value={
                values.usageStartDate && values.usageEndDate
                  ? format(values.usageStartDate, "dd/MM/yyyy") +
                    " - " +
                    format(values.usageEndDate, "dd/MM/yyyy")
                  : ""
              }
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              label={t("reports.period")}
              onClick={() => handleToggleUsageOpen()}
            />
            <IconButton
              onClick={() => handleToggleUsageOpen()}
              aria-label="toggle calendar"
              style={{ marginLeft: "8px" }}
            >
              <FaCalendarAlt />
            </IconButton>
            <DateRangePickerModal
              open={usageOpen}
              setOpen={setUsageOpen}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.customer")}</Form.Label>
          <Form.Select
            name="customer"
            value={values.customer}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.customer && errors.customer)}
          >
            <option key="" value="">
              {t("reports.select")}...
            </option>
            {!demoMode && (
              <>
                <option key={user.id} value={user.id}>
                  {user.role === "admin" && `${user.fullName} (Admin)`}
                  {user.role !== "admin" &&
                    user.role !== "subaccount" &&
                    `${user.fullName} (${user.company})`}
                  {user.role === "subaccount" &&
                    (() => {
                      const parent =
                        user.admin ||
                        user.tenant ||
                        user.manager ||
                        user.customer;
                      if (!parent) return user.fullName; // fallback
                      return `${user.fullName} (${parent.fullName} - ${parent.company})`;
                    })()}
                </option>
                {tenantsList?.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.fullName} ({tenant.company})
                  </option>
                ))}
                {managersList?.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.fullName} ({manager.company})
                  </option>
                ))}
                {customersList?.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName} ({customer.company})
                  </option>
                ))}
              </>
            )}
            {demoMode && (
              <>
                {dummyCustomers.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName}
                  </option>
                ))}
              </>
            )}
          </Form.Select>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "usage"} dimension="height">
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.simSearchCriteria")}</Form.Label>
          <Form.Select
            name="simSearchCriteriaType"
            value={values.simSearchCriteriaType}
            onChange={(e) => {
              handleChange(e);
              setFieldValue("imsi", "");
              setFieldValue("startImsi", "");
              setFieldValue("endImsi", "");
              setFieldValue("iccid", "");
            }}
            onBlur={handleBlur}
            isInvalid={Boolean(
              touched.simSearchCriteriaType && errors.simSearchCriteriaType
            )}
          >
            <option key="1" value="all">
              {t("reports.all")}
            </option>
            <option key="2" value="imsi">
              {t("reports.bySpecificImsi")}
            </option>
            <option key="3" value="imsiRange">
              {t("reports.byImsiRange")}
            </option>
            <option key="4" value="iccid">
              {t("reports.byIccid")}
            </option>
          </Form.Select>
        </Form.Group>
      </Collapse>
      <Collapse
        in={nextCollapse === "imsi" && reportTypeCollapse === "usage"}
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.imsi")}</Form.Label>
          <Form.Control
            type="text"
            name="imsi"
            value={values.imsi}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.imsi && errors.imsi)}
          />
        </Form.Group>
      </Collapse>
      <Collapse
        in={nextCollapse === "imsiRange" && reportTypeCollapse === "usage"}
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.startImsi")}</Form.Label>
          <Form.Control
            type="text"
            name="startImsi"
            value={values.startImsi}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.startImsi && errors.startImsi)}
          />
        </Form.Group>
      </Collapse>
      <Collapse
        in={nextCollapse === "imsiRange" && reportTypeCollapse === "usage"}
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.endImsi")}</Form.Label>
          <Form.Control
            type="text"
            name="endImsi"
            value={values.endImsi}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.endImsi && errors.endImsi)}
          />
        </Form.Group>
      </Collapse>
      <Collapse
        in={nextCollapse === "iccid" && reportTypeCollapse === "usage"}
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.iccid")}</Form.Label>
          <Form.Control
            type="text"
            name="iccid"
            value={values.iccid}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.iccid && errors.iccid)}
          />
        </Form.Group>
      </Collapse>
      <Collapse
        in={
          reportTypeCollapse === "invoiceDetail" ||
          reportTypeCollapse === "invoiceOverview"
        }
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>Invoice Number</Form.Label>
          <Form.Control
            type="text"
            name="invoiceNumber"
            value={values.invoiceNumber}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.invoiceNumber && errors.invoiceNumber)}
          />
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "invoice"} dimension="height">
        <Form.Group className="mb-3">
          <Form.Label>Invoice Number</Form.Label>
          <Form.Control
            type="text"
            value={values.invoiceNumber}
            name="invoiceNumber"
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.invoiceNumber && errors.invoiceNumber)}
          />
          {!!touched.invoiceNumber && (
            <Form.Control.Feedback type="invalid">
              {errors.invoiceNumber}
            </Form.Control.Feedback>
          )}
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "orders"} dimension="height">
        <Form.Group className="mb-3">
          {/* <Form.Label>{t("reports.period")}</Form.Label> */}
          <div className="d-flex mt-4">
            <TextField
              fullWidth
              variant="outlined"
              value={
                values.orderStartDate && values.orderEndDate
                  ? format(values.orderStartDate, "dd/MM/yyyy") +
                    " - " +
                    format(values.orderEndDate, "dd/MM/yyyy")
                  : ""
              }
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              label={t("reports.period")}
              onClick={() => handleToggleOrderOpen()}
            />
            <IconButton
              onClick={() => handleToggleOrderOpen()}
              aria-label="toggle calendar"
              style={{ marginLeft: "8px" }}
            >
              <FaCalendarAlt />
            </IconButton>
            <DateRangePickerModal
              open={orderOpen}
              setOpen={setOrderOpen}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        </Form.Group>
      </Collapse>
      <Collapse in={reportTypeCollapse === "invoice-list"} dimension="height">
        <Form.Group className="mb-3">
          {/* <Form.Label>{t("reports.period")}</Form.Label> */}
          <div className="d-flex mt-4">
            <TextField
              fullWidth
              variant="outlined"
              value={
                values.invoiceStartDate
                  ? format(values.invoiceStartDate, "dd/MM/yyyy")
                  : ""
              }
              InputProps={{ readOnly: true }}
              InputLabelProps={{ shrink: true }}
              label={t("reports.startDate")}
              onClick={() => handleToggleInvoiceListOpen()}
            />
            <IconButton
              onClick={() => handleToggleInvoiceListOpen()}
              aria-label="toggle calendar"
              style={{ marginLeft: "8px" }}
            >
              <FaCalendarAlt />
            </IconButton>
            <DatePickerModal
              open={invoiceListOpen}
              setOpen={setInvoiceListOpen}
              values={values}
              setFieldValue={setFieldValue}
            />
          </div>
        </Form.Group>
      </Collapse>
      <Collapse
        in={reportTypeCollapse === "customer-sim-overview"}
        dimension="height"
      >
        <Form.Group className="mb-3">
          <Form.Label>{t("reports.customer")}</Form.Label>
          <Form.Select
            name="customer"
            value={values.customer}
            onChange={handleChange}
            onBlur={handleBlur}
            isInvalid={Boolean(touched.customer && errors.customer)}
          >
            <option key="" value="">
              {t("reports.select")}...
            </option>

            {(user.role === "admin" ||
              (user.role === "subaccount" && user.admin != null)) && (
              <optgroup label="Tenants">
                {tenantsList?.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.fullName} ({tenant.company})
                  </option>
                ))}
              </optgroup>
            )}
            {(user.role === "admin" ||
              user.role === "tenant" ||
              (user.role === "subaccount" &&
                (user.admin != null || user.tenant != null))) && (
              <optgroup label="Managers">
                {managersList?.map((manager) => (
                  <option key={manager.id} value={manager.id}>
                    {manager.fullName} ({manager.company})
                  </option>
                ))}
              </optgroup>
            )}
            {(user.role === "admin" ||
              user.role === "tenant" ||
              user.role === "manager" ||
              (user.role === "subaccount" &&
                (user.admin != null ||
                  user.tenant != null ||
                  user.manager != null))) && (
              <optgroup label="Customers">
                {customersList?.map((customer) => (
                  <option key={customer.id} value={customer.id}>
                    {customer.fullName} ({customer.company})
                  </option>
                ))}
              </optgroup>
            )}
          </Form.Select>
        </Form.Group>
      </Collapse>
      {isDesktop && (
        <Button
          type="submit"
          disabled={isSubmitting}
          style={{
            "--dynamic-bg-color": whitelabel?.backgroundColor ?? "#0E1A2B",
            "--dynamic-font-color": whitelabel?.fontColor ?? "#ffffff",
            border: "none",
          }}
        >
          {isSubmitting ? "Loading..." : t("reports.view")}
        </Button>
      )}
      {!isDesktop && (
        <div className="d-flex justify-content-center mt-3">
          <Button
            type="submit"
            disabled={isSubmitting}
            style={{
              "--dynamic-bg-color": whitelabel?.backgroundColor ?? "#0E1A2B",
              "--dynamic-font-color": whitelabel?.fontColor ?? "#ffffff",
              border: "none",
              width: "100%",
              padding: "10px",
            }}
          >
            {isSubmitting ? "Loading..." : t("reports.view")}
          </Button>
        </div>
      )}
    </Form>
  );
};

export default ReportsFilterForm;
